import { lazy, Suspense, useEffect, useMemo } from 'react';
import { config } from './config';
// Cesium is heavy (~6 MB) and lives in its own chunk so the PWA can keep
// it out of the offline precache. Lazy-import keeps the chunk off the
// 2D-mode load path entirely. Same trick for LocateMeButton — it pulls
// Cesium in, so it must be lazy too (only renders in 3D anyway).
// Race the dynamic import against a short timeout. Offline (and iOS PWAs
// in particular) the SW falls through to a network fetch that can hang
// indefinitely — without this, Suspense never resolves and the user is
// stuck on the boot screen. The CesiumChunkBoundary in App's tree catches
// the rejection and reloads in 2D.
function loadWithTimeout<T>(loader: () => Promise<T>): Promise<T> {
  return Promise.race([
    loader(),
    new Promise<T>((_, reject) =>
      setTimeout(
        () => reject(new Error('chunk load timed out (likely offline)')),
        config.cesiumChunkTimeoutMs,
      ),
    ),
  ]);
}
const CesiumViewer = lazy(() =>
  loadWithTimeout(() => import('./viewer/CesiumViewer')).then((m) => ({
    default: m.CesiumViewer,
  })),
);
const LocateMeButton = lazy(() =>
  loadWithTimeout(() => import('./ui/LocateMeButton')).then((m) => ({
    default: m.LocateMeButton,
  })),
);
import { LeafletViewer } from './viewer/LeafletViewer';
import { CesiumChunkBoundary } from './ui/CesiumChunkBoundary';
import { HUD } from './ui/HUD';
import { PositionHUD } from './ui/PositionHUD';
// LotDetails is heavy (~30 KB Brotli incl. react-phone-number-input) and
// only renders content once a lot is selected — see `if (!lot) return null`
// at the top of LotDetails.tsx. Lazy-loading it removes that weight from
// the initial 2D bundle, addressing PSI's "Reduce unused JavaScript".
const LotDetails = lazy(() =>
  import('./ui/LotDetails').then((m) => ({ default: m.LotDetails })),
);
import { LowDataFooter } from './ui/LowDataFooter';
import { LanguageToggle } from './ui/LanguageToggle';
import { LowDataInfo } from './ui/LowDataInfo';
import { PhoneUrlButton } from './ui/PhoneUrlButton';
import { ShareButton } from './ui/ShareButton';
import { WarningBanner } from './ui/WarningBanner';
import { loadLotMeta } from './data/lotsApi';
import { isLowDataMode } from './viewer/lowDataMode';

export default function App() {
  // Toggling between low-data and full Cesium mode reloads the page
  // anyway, so a single read at mount is enough — useMemo makes that
  // explicit without dragging state in.
  const lowData = useMemo(() => isLowDataMode(), []);

  useEffect(() => {
    void loadLotMeta();
  }, []);
  return (
    <>
      {lowData ? (
        <LeafletViewer />
      ) : (
        <CesiumChunkBoundary>
          <Suspense fallback={null}>
            <CesiumViewer />
          </Suspense>
        </CesiumChunkBoundary>
      )}
      <Suspense fallback={null}>
        <LotDetails />
      </Suspense>
      <ShareButton />
      <PhoneUrlButton />
      <LanguageToggle />
      <WarningBanner />
      {/* Cesium-only HUDs — pointer-lock instructions don't apply to 2D. */}
      {!lowData && <HUD />}
      {!lowData && (
        <CesiumChunkBoundary>
          <Suspense fallback={null}>
            <LocateMeButton />
          </Suspense>
        </CesiumChunkBoundary>
      )}
      {/* Position presets + share work in both modes (PositionHUD branches
          internally on isLowDataMode). */}
      <PositionHUD />
      {lowData && <LowDataFooter />}
      {lowData && <LowDataInfo />}
    </>
  );
}
